import React from 'react';
import styled, { css, useTheme } from 'styled-components';
import { useSegment } from '@sparknz/journeys-common';
import {
  BaseProductCard,
  Box,
  Divider,
  Icon,
  ProductCardFeature,
  ProductCardPrice,
  ProductCardSection,
  Text,
} from '@sparknz/spark-pattern-library';
import { trackCustomLink } from '@sparknz/tracking';

import { RelatedOfferDetails } from 'components/shop/MobilePlans/typings/plans';
import { getPrice } from 'components/shop/MobilePlans/utils/getPrice.utils';
import { removePrices } from 'components/shop/MobilePlans/utils/removePrices.utils';
import { IconText } from 'components/shop/shared/components';
import { INFO_LAYER_CONTENT_QUERY_KEY } from 'shared/constants/query.constants';
import useQuery from 'shared/hooks/useQuery';

interface MinimalPlanCardProps {
  plan: RelatedOfferDetails;
}

const PlanEntitlement = styled(ProductCardFeature)`
  ${({ theme: { spacing } }) => css`
    margin-bottom: ${spacing.s2};
  `}
`;

const VIEW_KEY_CONDITIONS_ICON_TITLE = '*View key conditions information icon';

const MinimalPlanCard = ({ plan }: MinimalPlanCardProps) => {
  const { isBusiness } = useSegment();
  const { query, replaceQuery } = useQuery();
  const {
    palette: { baseColors },
  } = useTheme();

  const { name, contentKey, entitlements, price, id } = plan;

  const priceValue = price ? getPrice(price, isBusiness) : null;

  const openInfoLayer = (infoContentKey: string | null) => {
    if (!infoContentKey) {
      return;
    }

    replaceQuery({ ...query, [INFO_LAYER_CONTENT_QUERY_KEY]: infoContentKey });
  };

  return (
    <BaseProductCard dynamicTargetValue={id} keepMarketingTabHeight={false}>
      <ProductCardSection>
        {name ? (
          <Text mb="s1" variant="subheadingM">
            {removePrices(name)}
          </Text>
        ) : null}

        {priceValue ? (
          <ProductCardPrice
            price={{
              priceValue,
              pricePeriod: price?.frequency?.period || '',
              isGstExcl: isBusiness,
            }}
          />
        ) : null}

        {entitlements?.length ? (
          <Box pt="s3">
            {[...entitlements]
              .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder))
              .map((entitlement) => (
                <PlanEntitlement
                  key={entitlement.name}
                  iconUrl={entitlement.iconUrl ?? undefined}
                  title={entitlement.name}
                  description={entitlement.longDescription || ''}
                  handleInfoIconClick={
                    entitlement.contentKey
                      ? () => {
                          trackCustomLink(
                            `${entitlement.name} information icon`,
                          );
                          openInfoLayer(entitlement.contentKey);
                        }
                      : undefined
                  }
                />
              ))}
          </Box>
        ) : null}
      </ProductCardSection>

      <Divider />

      <Box pt="s2" mb="s2" mx="s4">
        <IconText
          variant="bodySDemi"
          onClick={() => {
            trackCustomLink(VIEW_KEY_CONDITIONS_ICON_TITLE);
            openInfoLayer(contentKey);
          }}
          icon={<Icon name="info_small" fill={baseColors.lupin} />}
          ariaLabel="View key conditions information layer"
          title={VIEW_KEY_CONDITIONS_ICON_TITLE}
        >
          *View key conditions
        </IconText>
      </Box>
    </BaseProductCard>
  );
};

export default MinimalPlanCard;
